// Ported from the ERP frontend's contexts/ThemeContext.jsx (same header
// color picker, font size, MUI component overrides — same spacing/
// colors so this app's admin section feels like one system with the
// ERP). One real change: the ERP's own copy hardcodes `mode = "light"`
// even though every styleOverride below branches on `mode === "dark"`
// — that dark-mode CSS was written but never wired to an actual toggle.
// Here `mode` is stateful and persisted, completing what was already
// there so "theme changing option" actually works.
import React, { createContext, useContext, useState, useMemo } from "react";
import { createTheme } from "@mui/material/styles";

const HEADER_COLORS = [
  { label: "Ocean Blue", value: "#0288d1" },
  { label: "Midnight", value: "#1a237e" },
  { label: "Forest", value: "#2e7d32" },
  { label: "Crimson", value: "#c62828" },
  { label: "Slate", value: "#37474f" },
  { label: "Purple", value: "#6a1b9a" },
  { label: "Teal", value: "#00695c" },
  { label: "Amber", value: "#e65100" },
  { label: "Indigo", value: "#4f46e5" },
  { label: "Violet", value: "#7c3aed" },
  { label: "Rose", value: "#e11d48" },
  { label: "Emerald", value: "#059669" },
  { label: "Sky", value: "#0ea5e9" },
  { label: "Fuchsia", value: "#c026d3" },
  { label: "Orange", value: "#ea580c" },
  { label: "Graphite", value: "#1e293b" },
];

const ThemeContext = createContext(null);

const load = (key, fallback) => {
  try { return JSON.parse(localStorage.getItem(key)) ?? fallback; } catch { return fallback; }
};

const FONT_SIZES = [
  { label: "Small", value: "small", scale: 0.875 },
  { label: "Medium", value: "medium", scale: 1 },
  { label: "Large", value: "large", scale: 1.125 },
];

export function ThemeContextProvider({ children }) {
  const [mode, setMode] = useState(() => load("themeMode", "light"));
  const [headerColor, setHeaderColor] = useState(() => load("headerColor", "#0288d1"));
  const [headerFixed, setHeaderFixed] = useState(() => load("headerFixed", true));
  const [fontSize, setFontSize] = useState(() => load("fontSize", "medium"));
  const primaryColor = headerColor;

  const toggleMode = () => {
    setMode((prev) => {
      const next = prev === "light" ? "dark" : "light";
      localStorage.setItem("themeMode", JSON.stringify(next));
      return next;
    });
  };

  const changeHeaderColor = (color) => {
    setHeaderColor(color);
    localStorage.setItem("headerColor", JSON.stringify(color));
  };

  const toggleHeaderFixed = () => {
    setHeaderFixed((prev) => {
      localStorage.setItem("headerFixed", JSON.stringify(!prev));
      return !prev;
    });
  };

  const changeFontSize = (size) => {
    setFontSize(size);
    localStorage.setItem("fontSize", JSON.stringify(size));
  };

  const scale = FONT_SIZES.find((f) => f.value === fontSize)?.scale ?? 1;
  const isDark = mode === "dark";

  const theme = useMemo(
    () =>
      createTheme({
        palette: {
          mode,
          primary: { main: primaryColor, contrastText: "#ffffff" },
          secondary: { main: "#64748b" },
          error: { main: "#d32f2f" },
          success: { main: "#2e7d32" },
          warning: { main: "#ed6c02" },
          background: {
            default: isDark ? "#0f172a" : "#f8f9fa",
            paper: isDark ? "#1e293b" : "#ffffff",
          },
          text: {
            primary: isDark ? "#e2e8f0" : "#111827",
            secondary: isDark ? "#94a3b8" : "#6b7280",
          },
          divider: isDark ? "#2d3748" : "#f1f5f9",
        },
        typography: {
          fontFamily: "'Inter', 'Noto Sans Tamil', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif",
          fontSize: 14 * scale,
          h5: { fontWeight: 600, fontSize: `${1.25 * scale}rem` },
          h6: { fontWeight: 600, fontSize: `${1 * scale}rem` },
          body1: { fontSize: `${0.875 * scale}rem` },
          body2: { fontSize: `${0.8125 * scale}rem` },
          caption: { fontSize: `${0.75 * scale}rem` },
        },
        shape: { borderRadius: 8 },
        components: {
          MuiButton: {
            styleOverrides: {
              root: { textTransform: "none", fontWeight: 500, borderRadius: 8, fontSize: "0.875rem", letterSpacing: "0.01em" },
              containedPrimary: {
                boxShadow: `0 1px 3px ${primaryColor}55`,
                "&:hover": { boxShadow: `0 3px 8px ${primaryColor}66` },
              },
            },
            defaultProps: { disableElevation: false },
          },
          MuiTextField: { defaultProps: { size: "small" } },
          MuiOutlinedInput: {
            styleOverrides: {
              root: {
                borderRadius: 8,
                fontSize: "0.875rem",
                backgroundColor: isDark ? "#1e293b" : "#ffffff",
                color: isDark ? "#e2e8f0" : "#111827",
                "& .MuiOutlinedInput-notchedOutline": { borderColor: isDark ? "#3f4b5e" : "#ccc" },
                "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: primaryColor },
                "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: primaryColor },
              },
              input: {
                "&::placeholder": { color: isDark ? "#64748b" : "#aaa", opacity: 1 },
              },
            },
          },
          MuiInputLabel: {
            styleOverrides: {
              root: {
                fontSize: "0.875rem",
                color: isDark ? "#94a3b8" : "#6b7280",
                "&.Mui-focused": { color: primaryColor },
              },
            },
          },
          MuiSelect: {
            styleOverrides: {
              icon: { color: isDark ? "#94a3b8" : "#6b7280" },
            },
          },
          MuiTableContainer: {
            styleOverrides: {
              root: { borderRadius: 10, border: "1px solid", borderColor: isDark ? "#2d3748" : "#e8ecf0", boxShadow: "0 1px 4px rgba(0,0,0,0.06)", overflow: "hidden" },
            },
          },
          MuiTableHead: {
            styleOverrides: {
              root: {
                "& .MuiTableCell-root": {
                  backgroundColor: `${primaryColor}14`,
                  color: primaryColor,
                  fontWeight: 600, fontSize: "0.75rem", letterSpacing: "0.06em",
                  textTransform: "uppercase", padding: "10px 14px",
                  borderBottom: `2px solid ${isDark ? "#2d3748" : "#e2e8f0"}`,
                  whiteSpace: "nowrap",
                },
              },
            },
          },
          MuiTableBody: {
            styleOverrides: {
              root: {
                "& .MuiTableCell-root": { fontSize: "0.8125rem", borderBottom: `1px solid ${isDark ? "#2d3748" : "#f1f5f9"}` },
                "& .MuiTableRow-root": {
                  transition: "background-color 0.15s ease",
                  "&:hover": { backgroundColor: isDark ? "rgba(255,255,255,0.04)" : "#f8fafc" },
                },
              },
            },
          },
          MuiAppBar: { styleOverrides: { root: { borderRadius: 0 } } },
          MuiPaper: {
            styleOverrides: {
              root: { borderRadius: 10 },
              elevation1: { boxShadow: "0 1px 4px rgba(0,0,0,0.07), 0 1px 2px rgba(0,0,0,0.05)" },
            },
          },
          MuiCard: { styleOverrides: { root: { borderRadius: 10 } } },
          // maxHeight/margin made explicit and viewport-unit-based (added —
          // reported as dialogs getting their top clipped on small screens).
          // MUI's own default (`calc(100% - 64px)` against a 32px margin)
          // assumes a full-height ancestor chain; on a short/mobile viewport
          // combined with this app's own sticky AdminHeader taking real
          // space at the top of the page, that default left too little
          // margin for tall dialog content to guarantee its own top (title
          // bar + close icon) stays on-screen. `dvh` (not `vh`) accounts for
          // mobile browsers' collapsing address bar, and the margin shrinks
          // on `xs` so a phone-width dialog still gets a real top/bottom gap
          // instead of nearly touching both edges.
          MuiDialog: {
            styleOverrides: {
              paper: {
                borderRadius: 12,
                boxShadow: "0 20px 60px rgba(0,0,0,0.14), 0 8px 24px rgba(0,0,0,0.08)",
                margin: 16,
                maxHeight: "calc(100dvh - 32px)",
                "@media (min-width: 600px)": { margin: 32, maxHeight: "calc(100dvh - 64px)" },
              },
            },
          },
          MuiDialogTitle: {
            styleOverrides: {
              // position: sticky (added) — belt-and-braces so the title/
              // close-icon row can never scroll out of view even if a
              // page's own dialog content isn't wrapped in a properly
              // scrollable DialogContent.
              root: { position: "sticky", top: 0, zIndex: 1, padding: "16px 20px", fontSize: "1rem", fontWeight: 600, backgroundColor: isDark ? "#1e1e1e" : "#fafbfc", borderBottom: `1px solid ${isDark ? "#2d3748" : "#f1f5f9"}` },
            },
          },
          MuiDialogContent: { styleOverrides: { root: { padding: "20px !important" } } },
          MuiDialogActions: {
            styleOverrides: {
              root: { padding: "12px 20px", gap: 8, backgroundColor: isDark ? "#1e1e1e" : "#fafbfc", borderTop: `1px solid ${isDark ? "#2d3748" : "#f1f5f9"}` },
            },
          },
          MuiDrawer: { styleOverrides: { paper: { borderRadius: 0, borderLeft: "1px solid", boxShadow: "-4px 0 20px rgba(0,0,0,0.08)" } } },
          MuiChip: {
            styleOverrides: {
              root: { borderRadius: 6, fontSize: "0.72rem", fontWeight: 600, height: 22, letterSpacing: "0.02em" },
              label: { padding: "0 8px" },
            },
          },
          MuiMenuItem: { styleOverrides: { root: { fontSize: "0.8125rem", padding: "7px 14px" } } },
          MuiTooltip: {
            styleOverrides: {
              tooltip: { borderRadius: 6, fontSize: "0.72rem", backgroundColor: "#1e293b", padding: "5px 10px" },
            },
          },
          MuiAlert: { styleOverrides: { root: { borderRadius: 8, fontSize: "0.875rem", fontWeight: 500, alignItems: "center" } } },
          MuiDivider: { styleOverrides: { root: { borderColor: isDark ? "#2d3748" : "#f1f5f9" } } },
          MuiFormControlLabel: { styleOverrides: { label: { fontSize: "0.875rem" } } },
        },
      }),
    [scale, mode, isDark, primaryColor]
  );

  return (
    <ThemeContext.Provider
      value={{
        mode, toggleMode,
        headerColor, changeHeaderColor, HEADER_COLORS,
        theme, headerFixed, toggleHeaderFixed,
        fontSize, changeFontSize, FONT_SIZES,
      }}
    >
      {children}
    </ThemeContext.Provider>
  );
}

export const useThemeContext = () => useContext(ThemeContext);
export { HEADER_COLORS };
